import React from "react";

// German-standard aluminum plate visualization (React SVG).
// Sizes:
//   long : 520 × 110 mm (Kfz standard)
//   short: 340 × 110 mm (verkürzt / e.g. off-road / spare)
//   moto : 180 × 200 mm (Kraftrad — 2-line format)
//
// Categories drive the coloured bands:
//   private   — blue EU-style left band, UM roundel
//   taxi      — ochre/mustard left band ("TAXI")
//   temporary — blue left band + yellow right expiry band (Kurzzeitkennzeichen)
//
// The component renders at physical scale (1 SVG unit = 1 mm). Consumers can
// scale it via width/height props. QR is intentionally omitted here (this is
// the aluminum plate, not the windshield sticker).
export const ALUMINUM_SIZES = {
    long:  { w: 520, h: 110, layout: "line" },
    short: { w: 340, h: 110, layout: "line" },
    moto:  { w: 180, h: 200, layout: "stacked" },
};

const CATEGORY_STYLES = {
    private:   { leftBand: "#003399", leftText: "#ffffff", leftLabel: "UM", numberColor: "#111111", rightBand: null,       suffix: null },
    taxi:      { leftBand: "#c69214", leftText: "#ffffff", leftLabel: "TAXI", numberColor: "#111111", rightBand: null,     suffix: null },
    temporary: { leftBand: "#003399", leftText: "#ffffff", leftLabel: "UM", numberColor: "#111111", rightBand: "#f7c60e",  suffix: null },
    motorcycle:{ leftBand: "#003399", leftText: "#ffffff", leftLabel: "UM", numberColor: "#111111", rightBand: null,       suffix: null },
    electric:  { leftBand: "#003399", leftText: "#ffffff", leftLabel: "UM", numberColor: "#111111", rightBand: null,       suffix: { char: "E", bg: "#22a06b", fg: "#ffffff" } },
};

export default function AluminumPlate({
    plateNumber = "B-UT 2026",
    size = "long",
    category = "private",
    expiryMmYy = "12/26",
    scale = 1,
    testid = "aluminum-plate",
}) {
    const dims = ALUMINUM_SIZES[size] || ALUMINUM_SIZES.long;
    const style = CATEGORY_STYLES[category] || CATEGORY_STYLES.private;
    const W = dims.w, H = dims.h;
    const leftBandW = size === "moto" ? W : Math.min(40, W * 0.09);
    const rightBandW = style.rightBand ? 22 : 0;
    const centerX = leftBandW + 4;
    const centerW = W - leftBandW - rightBandW - 4;

    // Font size: DIN Mittelschrift-ish scaled to available height.
    const isMoto = dims.layout === "stacked";
    const fontLine1 = isMoto ? H * 0.30 : H * 0.68;
    const fontLine2 = isMoto ? H * 0.45 : null;

    // Split plate for motorcycle stacked layout: "M-UT 4711" → ["M", "UT 4711"]
    let line1 = plateNumber, line2 = null;
    if (isMoto) {
        const parts = plateNumber.split(/[-\s]/);
        line1 = parts[0] || plateNumber;
        line2 = parts.slice(1).join(" ") || "";
    }

    const fit = scale === "fit";
    return (
        <svg
            data-testid={testid}
            viewBox={`0 0 ${W} ${H}`}
            width={fit ? "100%" : W * scale}
            height={fit ? "auto" : H * scale}
            style={{ display: "block", background: "#ffffff", borderRadius: 6, maxWidth: "100%" }}
            preserveAspectRatio="xMidYMid meet"
        >
            <defs>
                <radialGradient id="alu-holo" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#e0f7ff" />
                    <stop offset="45%" stopColor="#ffb3f2" />
                    <stop offset="75%" stopColor="#c6a3ff" />
                    <stop offset="100%" stopColor="#7ff0d1" />
                </radialGradient>
                <linearGradient id="alu-emblem" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#003399" />
                    <stop offset="50%" stopColor="#c0392b" />
                    <stop offset="100%" stopColor="#f7c60e" />
                </linearGradient>
                <pattern id="alu-laser" patternUnits="userSpaceOnUse" width="34" height="10" patternTransform="rotate(-8)">
                    <text x="0" y="8" fontSize="6" fill="#000" opacity="0.06" fontFamily="Inter, system-ui, sans-serif" letterSpacing="2">UTAL·MEA·</text>
                </pattern>
            </defs>
            {/* embossed border */}
            <rect x="1.5" y="1.5" width={W - 3} height={H - 3} rx={size === "moto" ? 8 : 6} ry={6} fill="#ffffff" stroke="#111" strokeWidth="2.4" />
            <rect x="4" y="4" width={W - 8} height={H - 8} rx={size === "moto" ? 6 : 4} ry={4} fill="#ffffff" stroke="#111" strokeWidth="0.4" opacity="0.35" />

            {/* Security: laser-mark ghost microtext across the whole face */}
            <rect x="4" y="4" width={W - 8} height={H - 8} fill="url(#alu-laser)" data-testid="sec-laser-marks" />

            {/* left / top colour band */}
            {isMoto ? (
                <>
                    <rect x="4" y="4" width={W - 8} height={26} fill={style.leftBand} />
                    <text x={W / 2} y={22} textAnchor="middle" fontSize="13" fontWeight="900" fontFamily="Inter, system-ui, sans-serif" fill={style.leftText} letterSpacing="2">UTAL MEA</text>
                </>
            ) : (
                <>
                    <rect x="4" y="4" width={leftBandW - 4} height={H - 8} fill={style.leftBand} />
                    <text x={leftBandW / 2 + 2} y={H - 14} textAnchor="middle" fontSize="8" fontWeight="900" fontFamily="Inter, system-ui, sans-serif" fill={style.leftText} letterSpacing="1">UM</text>
                    {/* EU-style ring of stars for private/temp (approx 12 tiny dots) */}
                    {(category === "private" || category === "temporary") && (
                        <g transform={`translate(${leftBandW / 2 + 2},${H * 0.32})`}>
                            {[...Array(12)].map((_, i) => {
                                const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
                                return <circle key={i} cx={Math.cos(a) * 9} cy={Math.sin(a) * 9} r="0.7" fill="#f7c60e" />;
                            })}
                        </g>
                    )}
                    {category === "taxi" && (
                        <text x={leftBandW / 2 + 2} y={H * 0.42} textAnchor="middle" fontSize="9" fontWeight="900" fontFamily="Inter, system-ui, sans-serif" fill={style.leftText} letterSpacing="1">{style.leftLabel}</text>
                    )}
                </>
            )}

            {/* right expiry band (temporary only) */}
            {style.rightBand && !isMoto && (
                <>
                    <rect x={W - rightBandW - 4} y="4" width={rightBandW} height={H - 8} fill={style.rightBand} />
                    <text
                        x={W - rightBandW / 2 - 4}
                        y={H / 2 - 4}
                        textAnchor="middle"
                        fontSize="15"
                        fontWeight="900"
                        fontFamily="Inter, system-ui, sans-serif"
                        fill="#111"
                    >{(expiryMmYy || "12/26").split("/")[0]}</text>
                    <text
                        x={W - rightBandW / 2 - 4}
                        y={H / 2 + 14}
                        textAnchor="middle"
                        fontSize="15"
                        fontWeight="900"
                        fontFamily="Inter, system-ui, sans-serif"
                        fill="#111"
                    >{(expiryMmYy || "12/26").split("/")[1] || "26"}</text>
                </>
            )}

            {/* plate number */}
            {isMoto ? (
                <>
                    <text x={W / 2} y={H * 0.55} textAnchor="middle" fontSize={fontLine1} fontWeight="900" fontFamily="Inter, system-ui, sans-serif" fill={style.numberColor} letterSpacing="2">{line1}</text>
                    <text x={W / 2} y={H * 0.93} textAnchor="middle" fontSize={fontLine2} fontWeight="900" fontFamily="Inter, system-ui, sans-serif" fill={style.numberColor} letterSpacing="2">{line2}</text>
                </>
            ) : (
                <text
                    x={centerX + centerW / 2}
                    y={H * 0.74}
                    textAnchor="middle"
                    fontSize={fontLine1}
                    fontWeight="900"
                    fontFamily="Inter, system-ui, sans-serif"
                    fill={style.numberColor}
                    letterSpacing="1"
                >{plateNumber}</text>
            )}

            {/* dimension label (tiny, bottom-right for reference) */}
            <text x={W - 6} y={H - 3} textAnchor="end" fontSize="2.6" fill="#888" fontFamily="Inter, system-ui, sans-serif">{W}×{H}mm</text>

            {/* SECURITY FEATURES (line-format plates) */}
            {!isMoto && (
                <>
                    {/* Multicolor round emblem (top-center) */}
                    <g data-testid="sec-emblem" transform={`translate(${leftBandW + 6}, 6)`}>
                        <circle cx="8" cy="8" r="7.2" fill="url(#alu-emblem)" stroke="#111" strokeWidth="0.3" />
                        <text x="8" y="10.2" textAnchor="middle" fontSize="5.5" fill="#fff" fontWeight="900" fontFamily="Inter, system-ui, sans-serif">UM</text>
                    </g>
                    {/* Hologram (top-right corner) */}
                    <g data-testid="sec-hologram" transform={`translate(${W - (style.rightBand ? 30 : 12) - 12}, 6)`}>
                        <rect width="10" height="10" rx="1" fill="url(#alu-holo)" stroke="#111" strokeWidth="0.3" />
                        <text x="5" y="7.5" textAnchor="middle" fontSize="4" fill="#fff" fontWeight="900" fontFamily="Inter, system-ui, sans-serif">✦</text>
                    </g>
                    {/* Mini QR code marker (bottom-left, below EU band) */}
                    <g data-testid="sec-qr" transform={`translate(${leftBandW + 4}, ${H - 14})`}>
                        <rect width="10" height="10" fill="#fff" stroke="#111" strokeWidth="0.3" />
                        {[[0,0],[1,0],[2,0],[0,1],[2,1],[0,2],[1,2],[2,2],[4,0],[6,0],[7,0],[5,1],[7,1],[4,2],[6,2],
                          [0,4],[2,4],[3,4],[5,4],[7,4],[1,5],[4,5],[6,5],[0,6],[2,6],[3,6],[5,6],[7,6],
                          [0,7],[1,7],[2,7],[0,8],[2,8],[4,7],[6,7],[4,8],[6,8],[7,8]
                        ].map(([x, y], i) => (
                            <rect key={i} x={0.5 + x} y={0.5 + y} width="1" height="1" fill="#111" />
                        ))}
                    </g>
                    {/* Serial number + barcode (bottom-right) */}
                    <g data-testid="sec-serial" transform={`translate(${W - (style.rightBand ? 32 : 12) - 46}, ${H - 8})`}>
                        <text x="0" y="4" fontSize="3.2" fontFamily="Inter, system-ui, sans-serif" fill="#111" letterSpacing="0.4">AB 1234567</text>
                        <g transform="translate(24, 0)">
                            {[1,2,1,3,1,2,3,1,2,1,3,2,1,3,1,2,1,3,1,2].map((w, i) => (
                                <rect key={i} x={i * 1.1} y="0" width={w * 0.35} height="5" fill="#111" />
                            ))}
                        </g>
                    </g>
                </>
            )}
            {/* SECURITY FEATURES (motorcycle) */}
            {isMoto && (
                <>
                    <g data-testid="sec-hologram" transform={`translate(${W - 16}, ${H - 44})`}>
                        <rect width="10" height="10" rx="1" fill="url(#alu-holo)" stroke="#111" strokeWidth="0.3" />
                        <text x="5" y="7.5" textAnchor="middle" fontSize="4" fill="#fff" fontWeight="900">✦</text>
                    </g>
                    <g data-testid="sec-qr" transform={`translate(6, ${H - 44})`}>
                        <rect width="10" height="10" fill="#fff" stroke="#111" strokeWidth="0.3" />
                        {[[0,0],[2,0],[0,2],[2,2],[4,4],[6,4],[4,6],[6,6],[0,7],[2,7],[7,0],[7,2]].map(([x, y], i) => (
                            <rect key={i} x={0.5 + x} y={0.5 + y} width="1" height="1" fill="#111" />
                        ))}
                    </g>
                </>
            )}

            {/* Electric E-stamp (green square with white E after the number) */}
            {style.suffix && !isMoto && (
                <g transform={`translate(${W - (style.rightBand ? 32 : 12) - 22},${H / 2 - 10})`}>
                    <rect width="20" height="20" fill={style.suffix.bg} rx="1.5" />
                    <text x="10" y="15" textAnchor="middle" fontSize="14" fontWeight="900" fill={style.suffix.fg} fontFamily="Inter, system-ui, sans-serif">{style.suffix.char}</text>
                </g>
            )}
            {style.suffix && isMoto && (
                <g transform={`translate(${W - 30},${H - 30})`}>
                    <rect width="22" height="22" fill={style.suffix.bg} rx="2" />
                    <text x="11" y="17" textAnchor="middle" fontSize="15" fontWeight="900" fill={style.suffix.fg} fontFamily="Inter, system-ui, sans-serif">{style.suffix.char}</text>
                </g>
            )}
        </svg>
    );
}
